import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { distinctUntilChanged, Subscription } from "rxjs";
import {
  CampaignCodeClient,
  CampaignCodeResponse,
  DealerViewModel,
  DiscountTypeEnum,
  FindCampaignCodeResponse,
  MediaCategoryTypeNameEnum,
  MediaTypeNameEnum,
  ProductViewModelBase,
  ShoppingCartItemViewModel,
  ShoppingCartViewModel,
} from "src/domain/client";
import SettingNames from "src/domain/extentions/setting-names";
import { ChannelRequestService } from "src/services/channel-request.service";
import { ModelAndDealerService } from "src/services/model-and-dealer.service";
import { ShoppingCartService } from "src/services/shopping-cart.service";
import { ThemeService } from "src/services/theme.service";

@Component({
  selector: "vw-shopping-cart",
  templateUrl: "./shopping-cart.component.html",
})
export class ShoppingCartComponent implements OnInit, OnDestroy {
  @Input() showCampaignCodeRow: boolean = false;

  selectedDealer: DealerViewModel | null = null;
  private subscription = new Subscription();

  mediaCategoryTypeNames = MediaCategoryTypeNameEnum;
  mediaTypes = MediaTypeNameEnum;
  settingNames = SettingNames;

  campaignCodeResponse!: FindCampaignCodeResponse;
  isInvalidCampaignCode = false;
  isCampaignCodeApplied = false;

  campaignCodeForm = new FormGroup({
    campaignCode: new FormControl<string | undefined>(
      "",
      Validators.maxLength(100),
    ),
  });

  private originalPricesMap = new Map<number, number>();
  originalShoppingCart?: ShoppingCartViewModel;
  totalPriceOriginal = 0;

  constructor(
    public modelAndDealerService: ModelAndDealerService,
    public shoppingCartService: ShoppingCartService,
    public themeService: ThemeService,
    public channelRequestService: ChannelRequestService,
  ) {}

  ngOnInit() {
    this.subscription.add(
      this.modelAndDealerService.selectedDealer$
        .pipe(distinctUntilChanged())
        .subscribe(() => {
          this.removeCampaignCode();
        }),
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  async validateAndApplyOrRemoveCampaignCode(): Promise<void> {
    if (this.isCampaignCodeApplied) {
      this.removeCampaignCode();
      return;
    }

    const campaignCodeToValidate =
      this.campaignCodeForm.controls.campaignCode.value ?? "";

    const response = await new CampaignCodeClient().findCampaignCode(
      campaignCodeToValidate,
      this.modelAndDealerService.selectedDealer?.number,
    );

    this.campaignCodeResponse = response;

    if (response.isValidCampaignCode) {
      this.isInvalidCampaignCode = false;
      this.applyCampaignCode(response.campaignCodeDetails);
    } else {
      this.isInvalidCampaignCode = true;
    }
  }

  applyCampaignCode(campaignCodeDetails: CampaignCodeResponse | undefined) {
    this.originalShoppingCart = JSON.parse(
      JSON.stringify(this.shoppingCartService.shoppingCart),
    );
    const shoppingCartItems =
      this.shoppingCartService.shoppingCart?.shoppingCartItems;

    const applicableItems = campaignCodeDetails?.isValidForCampaignProducts
      ? (shoppingCartItems ?? [])
      : (shoppingCartItems?.filter(
          (item) => item.product?.isCampaign !== true,
        ) ?? []);

    if (!campaignCodeDetails || !shoppingCartItems) return;

    applicableItems.forEach((item) => {
      if (
        item.shoppingCartItemId &&
        item.product?.price?.salesPrice !== undefined
      ) {
        if (!this.originalPricesMap.has(item.shoppingCartItemId)) {
          this.originalPricesMap.set(
            item.shoppingCartItemId,
            item.product.price.salesPrice,
          );
        }
      }
    });

    switch (campaignCodeDetails.discountType) {
      case DiscountTypeEnum.Fixed:
        this.applyFixedDiscount(
          applicableItems,
          campaignCodeDetails.discount ?? 0,
        );
        break;
      case DiscountTypeEnum.Percentage:
        this.applyPercentageDiscount(
          applicableItems,
          campaignCodeDetails.discount ?? 0,
        );
        break;
    }
    this.isCampaignCodeApplied = true;
    this.shoppingCartService.campaignCodeId =
      campaignCodeDetails.campaignCodeId ?? 0;

    this.updateTotalPriceComparison();
  }

  decreaseQuantity(shoppingCartItemId: number): void {
    this.shoppingCartService.decreaseQuantity(shoppingCartItemId);
  }

  increaseQuantity(event: MouseEvent, product: ProductViewModelBase): void {
    if (
      product.productId != null &&
      product.sku != null &&
      !(
        event.currentTarget instanceof Element &&
        event.currentTarget.classList.contains("disabled")
      )
    ) {
      this.shoppingCartService.addShoppingCartItem(
        product.productId,
        product.sku,
        product.minimumQuantity,
        product.media,
        1,
      );
    }
  }

  deleteItem(shoppingCartItemId: number): void {
    this.shoppingCartService.deleteItem(shoppingCartItemId);
  }

  private applyFixedDiscount(
    applicableItems: ShoppingCartItemViewModel[],
    discountAmount: number,
  ) {
    if (applicableItems.length === 0 || discountAmount <= 0) return;

    const totalApplicablePrice = applicableItems.reduce(
      (sum, item) =>
        sum + (item.product?.price?.salesPrice ?? 0) * (item.quantity ?? 1),
      0,
    );

    if (totalApplicablePrice === 0) return;

    applicableItems.forEach((item) => {
      if (item.product?.price?.salesPrice) {
        const itemPrice =
          (item.product.price.salesPrice ?? 0) * (item.quantity ?? 1);
        const discountShare =
          (itemPrice / totalApplicablePrice) * discountAmount;
        item.product.price.salesPrice = Math.max(
          item.product.price.salesPrice - discountShare / (item.quantity ?? 1),
          0,
        );
      }
    });
  }

  private applyPercentageDiscount(
    applicableItems: ShoppingCartItemViewModel[],
    discountPercentage: number,
  ) {
    if (applicableItems.length === 0 || discountPercentage <= 0) return;

    applicableItems.forEach((item) => {
      if (item.product?.price?.salesPrice !== undefined) {
        item.product.price.salesPrice = Math.max(
          item.product.price.salesPrice * (1 - discountPercentage),
          0,
        );
      }
    });
  }

  private updateTotalPriceComparison() {
    const shoppingCartItems =
      this.shoppingCartService.shoppingCart?.shoppingCartItems ?? [];

    this.totalPriceOriginal = shoppingCartItems.reduce(
      (sum, item) =>
        sum +
        (this.originalPricesMap.get(item.shoppingCartItemId ?? 0) ??
          item.product?.price?.salesPrice ??
          0) *
          (item.quantity ?? 1),
      0,
    );
  }

  private removeCampaignCode(): void {
    if (this.originalShoppingCart) {
      this.shoppingCartService.shoppingCart = JSON.parse(
        JSON.stringify(this.originalShoppingCart),
      );
      this.originalShoppingCart = undefined;
      this.originalPricesMap.clear();
      this.shoppingCartService.campaignCodeId = 0;
      this.campaignCodeForm.reset();
      this.isCampaignCodeApplied = false;
      this.isInvalidCampaignCode = false;
    }
  }
}
